import { DecisionType, Rule } from "@/graphql/__generated__/graphql";
import { Card } from "@/components/ui/card";
import { useTranslation } from "react-i18next";
import { useIntl } from "react-intl";
import { useDeleteRuleMutationHook, useUpdateRuleMutationHook } from "@/hooks";
import { Skeleton } from "@/components/ui/skeleton";
import { useLocation } from "react-router-dom";
import RuleComponent from "./RuleComponent";
import { JSX } from "react";

interface RenderRulesComponentProps {
  rules: Rule[];
  loading: boolean;
}

/**
 * Main component that renders a list of rules
 * @param {Object} props - Component props
 * @param {Rule[]} props.rules - Array of rules to render
 * @param {boolean} props.loading - Loading state
 * @returns {JSX.Element} Rendered component
 */
function RenderRulesComponent({ rules, loading }: RenderRulesComponentProps): JSX.Element {
  const { t } = useTranslation(["translation", "common", "components"]);
  const intl = useIntl();
  const { pathname } = useLocation();
  const ruleType = pathname.includes("marginrules") ? DecisionType.Margin : DecisionType.Bid;

  const { deleteRuleMutation, loading: deleteRuleLoading } = useDeleteRuleMutationHook(ruleType);
  const { updateRuleMutation, loading: updateRuleLoading } = useUpdateRuleMutationHook();

  const isProcessing = deleteRuleLoading || updateRuleLoading;

  if (rules.length === 0) {
    return (
      <Card className="p-4 text-sm text-slate-400">
        <p>{t("settings.rulebuilder.view.norules")}</p>
      </Card>
    );
  }

  if (loading) {
    return (
      <div className="flex flex-col gap-4">
        <Skeleton className="h-[100px] w-full" />
        <Skeleton className="h-[100px] w-full" />
        <Skeleton className="h-[100px] w-full" />
      </div>
    );
  }

  return (
    <div className="flex flex-col">
      {rules.map((rule, index) => (
        <div key={rule.id}>
          <div className="flex items-center justify-center">
            {index === 0 ? (
              <div className="text-base font-bold text-slate-600 my-2 flex flex-col items-center">
                {index !== 0 && (
                  <div className="w-0.5 h-2 bg-slate-200 mb-1"></div>
                )}
                <span>{t("settings.rulebuilder.if")}</span>
                <div className="w-0.5 h-2 bg-slate-200 mt-1"></div>
              </div>
            ) : (
              <div className="text-base font-bold text-slate-600 my-2 flex flex-col items-center">
                <div className="w-0.5 h-2 bg-slate-200 mb-1"></div>
                <span>{t("settings.rulebuilder.or")}</span>
                <div className="w-0.5 h-2 bg-slate-200 mt-1"></div>
              </div>
            )}
          </div>
          <RuleComponent
            rule={rule}
            deleteRuleMutation={deleteRuleMutation}
            updateRuleMutation={updateRuleMutation}
            t={t}
            intl={intl}
            isProcessing={isProcessing}
          />
        </div>
      ))}
    </div>
  );
}

export default RenderRulesComponent;
